import { Link } from 'react-router-dom'
import { useOptionalT } from '../i18n/LocaleProvider'
import { BlogMetas, type BlogMeta } from './BlogMetas'

export type { BlogMeta }

export interface BlogCardProps {
  image: string
  /**
   * Describes the picture. Defaults to the generic `Blog Image` the placeholders have
   * always carried; pass one wherever the image is a real photograph and means something.
   */
  imageAlt?: string
  title: string
  to: string
  metas: BlogMeta[]
  /**
   * `grid`    — the home page's three cards and any other picture grid: a date badge over
   *             the image, and a `.ul-blog-btn` "Read More" with a chevron.
   * `compact` — every post on the blog index: the same content laid out as a row, with the
   *             photograph as a small thumbnail beside the copy rather than a 890×600
   *             picture above it. An index of a dozen posts, each opening with a picture
   *             the width of the column, is one post per screen and no way to scan the
   *             list. The markup is identical — the layout is entirely in the class.
   *
   * There was a third, `inner`, which is what the index used to be: no date badge, the
   * image above the copy at full width, and a `.ul-btn` under the excerpt. It came off on
   * 2026-09-19 with the class it emitted. Its other consumer was the article header on a
   * post's own page, which reused the card's classes for a layout that is not a card; that
   * page has its own now.
   */
  variant?: 'grid' | 'compact'
  /** `grid` only. */
  date?: { number: string; txt: string }
  /** `compact` only. */
  excerpt?: string
  ctaLabel?: string
}

/**
 * A `.ul-blog.ul-blog-2` card.
 *
 * The meta row is shared with `blog-details` via `BlogMetas` — including the grid
 * variant's vestigial Tailwind classes, which that component documents.
 */
export function BlogCard({
  image,
  imageAlt: imageAltProp,
  title,
  to,
  metas,
  variant = 'grid',
  date,
  excerpt,
  ctaLabel: ctaLabelProp,
}: BlogCardProps) {
  const t = useOptionalT()
  const imageAlt = imageAltProp ?? t('ul.blogCard.imageAlt')
  const ctaLabel = ctaLabelProp ?? t('ul.blogCard.cta')
  const isCompact = variant === 'compact'

  return (
    <div className={isCompact ? 'ul-blog ul-blog-2 ul-blog-compact' : 'ul-blog ul-blog-2'}>
      <div className="ul-blog-img">
        <img src={image} alt={imageAlt} />
        {!isCompact && date && (
          <div className="date">
            <span className="number">{date.number}</span>
            <span className="txt">{date.txt}</span>
          </div>
        )}
      </div>

      <div className="ul-blog-txt">
        <BlogMetas metas={metas} withLegacyTextClasses={!isCompact} />

        <Link to={to} className="ul-blog-title">
          {title}
        </Link>

        {/* The text link in both variants, never the `.ul-btn` pill: a row this size with
            a full button in it is mostly button, and density is the point of `compact`. */}
        {isCompact && <p className="ul-blog-excerpt">{excerpt}</p>}
        <Link to={to} className="ul-blog-btn">
          {ctaLabel}{' '}
          <span className="icon">
            <i className="flaticon-next" />
          </span>
        </Link>
      </div>
    </div>
  )
}
